import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Elements } from "@stripe/react-stripe-js";
import { api, formatApiError } from "@/lib/http";
import { loadClinicStripe } from "@/lib/stripeClient";
import { NewCardField } from "@/components/booking/CheckoutDialog";
import { useCustomMethods } from "@/components/booking/PaymentMethodsCard";
import { toast } from "sonner";
import { Wallet, CreditCard, Banknote, Link2, X, Copy, ExternalLink, Keyboard, ArrowLeft } from "lucide-react";

export const DepositDialog = ({ booking, onClose }) => {
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [keyIn, setKeyIn] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [useSaved, setUseSaved] = useState("");
  const [consent, setConsent] = useState(true);
  const cardRef = useRef(null);
  const customMethods = useCustomMethods();

  useEffect(() => {
    if (!booking?.id) return;
    api.get(`/bookings/${booking.id}/deposit-info`).then(({ data }) => {
      setInfo(data);
      setAmount(String(data.suggested || ""));
      if (data.cards?.[0]) setUseSaved(data.cards[0].id);
    }).catch(() => setInfo({ suggested: 0, currency: "AUD", client_credit: 0, stripe_connected: false, cards: [] }));
  }, [booking]);

  useEffect(() => {
    if (keyIn && !stripePromise) loadClinicStripe().then((p) => setStripePromise(p)).catch(() => toast.error("Couldn't load the secure card form"));
  }, [keyIn, stripePromise]);

  const act = async (action, method, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/deposit-action`, {
        action, amount: parseFloat(amount) || 0, method: method || "cash", origin_url: window.location.origin, ...extra,
      });
      if (action === "link" && data.checkout_url) {
        setLinkUrl(data.checkout_url);
        toast.success("Payment link ready — copy it to your client");
      } else {
        toast.success(action === "decline" ? "No deposit taken" : action === "rollover" ? "Deposit rolled over from credit" : action === "charge" ? "Card charged — deposit taken" : "Deposit recorded");
        onClose(true);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't process deposit"); }
    finally { setBusy(false); }
  };

  const chargeCard = async () => {
    if (!info?.client_id) return toast.error("Save the client to their record first, then key in the card");
    if (!(parseFloat(amount) > 0)) return toast.error("Enter a deposit amount");
    setBusy(true);
    try {
      let pm = useSaved;
      if (!pm) {
        if (!consent) { setBusy(false); return toast.error("Tick the consent box to charge & save the card"); }
        if (!cardRef.current) { setBusy(false); return toast.error("Card form isn't ready yet"); }
        const { data: intent } = await api.post(`/clients/${info.client_id}/cards/intent`);
        const res = await cardRef.current.confirm(intent.client_secret);
        if (res.error) { setBusy(false); return toast.error(res.error.message || "Card couldn't be verified"); }
        pm = res.pm;
      }
      await act("charge", "card", { payment_method: pm });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't charge the card"); setBusy(false); }
  };

  const cur = info?.currency || "AUD";
  const amt = parseFloat(amount) || 0;

  return (
    <Dialog open={!!booking} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="deposit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" />Deposit for {booking?.name || "this booking"}</DialogTitle>
          <DialogDescription>Roll over existing credit, take a new deposit, or skip.</DialogDescription>
        </DialogHeader>

        {!info ? <div className="py-6 text-center text-muted-foreground text-sm">Loading…</div> : linkUrl ? (
          <div className="space-y-3" data-testid="deposit-link-ready">
            <p className="text-sm text-muted-foreground">Send this secure payment link to your client to pay the {cur} {amt.toFixed(2)} deposit:</p>
            <div className="flex gap-2">
              <Input value={linkUrl} readOnly className="h-11 rounded-lg text-xs" data-testid="deposit-link-input" />
              <Button variant="outline" className="h-11 rounded-lg shrink-0" onClick={() => { navigator.clipboard.writeText(linkUrl); toast.success("Link copied"); }} data-testid="deposit-link-copy"><Copy className="h-4 w-4" /></Button>
              <a href={linkUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="h-11 rounded-lg shrink-0"><ExternalLink className="h-4 w-4" /></Button></a>
            </div>
            <Button className="w-full h-11 rounded-lg" onClick={() => onClose(true)} data-testid="deposit-link-done">Done</Button>
          </div>
        ) : keyIn ? (
          <div className="space-y-3" data-testid="deposit-keyin">
            <button type="button" onClick={() => setKeyIn(false)} className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground" data-testid="deposit-keyin-back"><ArrowLeft className="h-3.5 w-3.5" />Back</button>
            <div className="flex items-baseline justify-between"><span className="text-sm text-muted-foreground">Charging</span><span className="font-heading font-bold text-xl tabular-nums" data-testid="deposit-keyin-amount">{cur} {amt.toFixed(2)}</span></div>
            {info.cards?.length > 0 && (
              <div className="space-y-1.5" data-testid="deposit-saved-cards">
                {info.cards.map((c) => (
                  <label key={c.id} className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${useSaved === c.id ? "border-primary bg-primary/5" : ""}`}>
                    <input type="radio" name="dep-card" checked={useSaved === c.id} onChange={() => setUseSaved(c.id)} className="accent-primary" data-testid={`deposit-saved-card-${c.last4}`} />
                    <CreditCard className="h-4 w-4 text-muted-foreground" />{c.brand} •••• {c.last4} <span className="text-xs text-muted-foreground ml-auto">{c.exp_month}/{c.exp_year}</span>
                  </label>
                ))}
                <label className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${!useSaved ? "border-primary bg-primary/5" : ""}`}>
                  <input type="radio" name="dep-card" checked={!useSaved} onChange={() => setUseSaved("")} className="accent-primary" data-testid="deposit-new-card-option" />
                  <Keyboard className="h-4 w-4 text-muted-foreground" />Key in a new card
                </label>
              </div>
            )}
            {!useSaved && (
              <div className="space-y-2">
                {stripePromise ? <Elements stripe={stripePromise}><NewCardField ref={cardRef} /></Elements> : <div className="text-sm text-muted-foreground">Loading secure card field…</div>}
                <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="deposit-card-consent" />
                  <span>The client authorises this deposit and saving the card securely for future visits.</span>
                </label>
              </div>
            )}
            <Button className="w-full h-11 rounded-lg" onClick={chargeCard} disabled={busy || (!useSaved && !stripePromise)} data-testid="deposit-charge-button">
              {busy ? "Charging…" : `Charge ${cur} ${amt.toFixed(2)}`}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Deposit amount ({cur})</Label>
              <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-lg text-lg font-semibold" data-testid="deposit-amount" />
            </div>

            <div className="space-y-2">
              <button disabled={busy || !info.client_credit || info.client_credit <= 0} onClick={() => act("rollover")}
                className="w-full flex items-center gap-3 p-3 rounded-xl border text-left hover:border-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="deposit-rollover">
                <Wallet className="h-5 w-5 text-primary shrink-0" />
                <div><div className="font-medium text-sm">Roll over existing credit</div><div className="text-xs text-muted-foreground">{info.client_credit > 0 ? `${cur} ${info.client_credit.toFixed(2)} available` : "No credit on file"}</div></div>
              </button>

              <div className="rounded-xl border p-3 space-y-2">
                <div className="font-medium text-sm flex items-center gap-2"><Banknote className="h-4 w-4 text-primary" />Take a new deposit</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => act("manual", "cash")} className="rounded-lg justify-start" data-testid="deposit-cash"><Banknote className="h-4 w-4 mr-1.5" />Cash</Button>
                  {customMethods.map((m) => <Button key={m.id} variant="outline" size="sm" disabled={busy} onClick={() => act("manual", m.id)} className="rounded-lg justify-start" data-testid={`deposit-method-${m.id}`} title="Already paid — just record it"><CreditCard className="h-4 w-4 mr-1.5" />{m.name}</Button>)}
                  <Button variant="outline" size="sm" disabled={busy || !info.stripe_connected} onClick={() => setKeyIn(true)} className="rounded-lg justify-start" data-testid="deposit-card-keyin" title={info.stripe_connected ? "Type the card details in now" : "Connect Stripe to key in cards"}><Keyboard className="h-4 w-4 mr-1.5" />Key in card</Button>
                  <Button variant="outline" size="sm" disabled={busy || !info.stripe_connected} onClick={() => act("link")} className="rounded-lg justify-start" data-testid="deposit-link" title={info.stripe_connected ? "" : "Connect Stripe to send a link"}><Link2 className="h-4 w-4 mr-1.5" />Send pay link</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">{info.stripe_connected ? "Your own payment methods (Setup → Business → Payment methods) only record a payment you've already taken." : "Connect Stripe to key in cards or send a card payment link."}</p>
              </div>

              <button disabled={busy} onClick={() => act("decline")}
                className="w-full flex items-center gap-3 p-3 rounded-xl border text-left hover:border-destructive/50 transition-colors" data-testid="deposit-decline">
                <X className="h-5 w-5 text-muted-foreground shrink-0" />
                <div><div className="font-medium text-sm">No deposit</div><div className="text-xs text-muted-foreground">Skip and keep the booking</div></div>
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
